import { createStore } from 'zustand';
import { shallow } from 'zustand/vanilla/shallow';
import { useStoreWithEqualityFn } from 'zustand/traditional';

export type FutureSecrets = {
  privateApiKey: string;
  bearerToken: string;
  tokenExpiration: number;
  isExpired: boolean;
};

type FutureSecretsStore = {
  secrets: FutureSecrets | null;
  actions: {
    setFutureSecrets: (
      futureSecretsValues: Pick<FutureSecrets, 'privateApiKey' | 'bearerToken'> | null
    ) => void;
    updateIsExpiredStatus: () => void;
  };
};

export const extractTokenExpiration = (bearerToken: string) => {
  let payload: unknown = null;
  try {
    payload = JSON.parse(atob(bearerToken.split('.')[1]));
  } catch {
    throw new Error('Not a valid bearer token');
  }
  const tokenExpiration =
    !!payload && typeof payload === 'object' && 'exp' in payload && typeof payload.exp === 'number'
      ? payload.exp * 1e3
      : undefined;
  if (!tokenExpiration) {
    throw new Error("Couldn't parse expiration time");
  }
  return tokenExpiration;
};

const futureSecretsStore = createStore<FutureSecretsStore>((set, getStore) => ({
  // Initial State
  secrets: null,
  // Actions
  actions: {
    updateIsExpiredStatus: () => {
      const secrets = getStore().secrets;
      const tokenExpiration = secrets?.tokenExpiration;
      const nowTimestamp = Date.now();
      if (!tokenExpiration || secrets?.isExpired === false || tokenExpiration > nowTimestamp) {
        return;
      }
      set({
        secrets: {
          ...secrets,
          isExpired: true
        }
      });
    },
    setFutureSecrets: (
      futureSecretsValues: Pick<FutureSecrets, 'privateApiKey' | 'bearerToken'> | null
    ) => {
      if (!futureSecretsValues) {
        set({
          secrets: null
        });
        return;
      }
      if (typeof futureSecretsValues !== 'object') {
        return;
      }
      const { bearerToken, privateApiKey } = futureSecretsValues;
      if (typeof bearerToken === 'string' && typeof privateApiKey === 'string') {
        try {
          try {
            JSON.parse(atob(bearerToken.split('.')[1]));
          } catch {
            throw new Error('Not a valid bearer token');
          }
          const tokenExpiration = extractTokenExpiration(bearerToken);
          const nowTimestamp = Date.now();
          const isExpired = !tokenExpiration || tokenExpiration <= nowTimestamp;
          const futureSecrets = {
            bearerToken,
            privateApiKey,
            tokenExpiration,
            isExpired
          };
          set({ secrets: futureSecrets });

          if (!isExpired) {
            // update state when it expires
            setTimeout(() => {
              const store = getStore();
              store.actions.updateIsExpiredStatus();
            }, tokenExpiration - nowTimestamp);
          }
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Failed to update secrets related to getting future products', error);
        }
      }
    }
  }
}));

// Note: while the input type is generally expected we have a bit of extra validation here since we don't have runtime validation of the inputs from developer tools
export const { setFutureSecrets } = futureSecretsStore.getState().actions;

const selectFutureSecrets = (state: FutureSecretsStore) => state.secrets;

export const useFutureSecrets = () =>
  useStoreWithEqualityFn(futureSecretsStore, selectFutureSecrets, shallow);

export const getFutureSecrets = () => selectFutureSecrets(futureSecretsStore.getState());
